
import { ApiFactory } from '@/api/api-factory';
import Analytics from '@/lib/analytics';

/**
 * Service for collecting and analyzing SEO-related data
 */
export const SeoDataService = {
  /**
   * Record a search query for SEO analysis
   * @param query The search query
   * @param resultsCount Number of results returned
   * @param source Where the search was performed (navbar, homepage, etc.)
   */
  recordSearchQuery: async (query: string, resultsCount: number, source: string = 'unknown') => {
    try {
      // Track in analytics
      Analytics.track('SEO Search Data', {
        query,
        results_count: resultsCount,
        source,
        timestamp: new Date().toISOString()
      });
      
      // Store in database for analysis
      await ApiFactory.getClient().recordSearchQuery(query, resultsCount, source);
      
      // Log for debugging
      console.log(`[SEO] Recorded search query: "${query}" with ${resultsCount} results from ${source}`);
    } catch (error) {
      console.error('Error recording search query for SEO:', error);
    }
  },
  
  /**
   * Get trending search terms for SEO content planning
   * @param limit Number of terms to return
   * @param days Number of days to look back
   */
  getTrendingSearchTerms: async (limit: number = 10, days: number = 30): Promise<{term: string, count: number}[]> => {
    try {
      return await ApiFactory.getClient().getTrendingSearchTerms(limit, days);
    } catch (error) {
      console.error('Error fetching trending search terms:', error);
      return [];
    }
  },
  
  /**
   * Get search terms with zero results for content gap analysis
   * @param limit Number of terms to return
   * @param days Number of days to look back
   */
  getZeroResultSearchTerms: async (limit: number = 10, days: number = 30): Promise<{term: string, count: number}[]> => {
    try {
      return await ApiFactory.getClient().getZeroResultSearchTerms(limit, days);
    } catch (error) {
      console.error('Error fetching zero result search terms:', error);
      return [];
    }
  },
  
  /**
   * Get location-based search analytics for regional content strategy
   */
  getLocationSearchAnalytics: async (): Promise<{location: string, count: number}[]> => {
    try {
      return await ApiFactory.getClient().getLocationSearchAnalytics();
    } catch (error) {
      console.error('Error fetching location search analytics:', error);
      return [];
    }
  }
};
